import moment from 'moment-timezone'

export const defaultCCNumberFormat = /(\d{1,4})/g
export const defaultCCNumberMask = '____ ____ ____ ____'

export interface CreditCardInfo {
  type: CreditCardType
  pattern: RegExp
  format: RegExp
  lengths: number[]
  cvcLengths: number[]
  luhn: boolean
  mask: string
}

export type CreditCardType =
  | 'amex'
  | 'dankort'
  | 'dinersclub'
  | 'discover'
  | 'elo'
  | 'hipercard'
  | 'jcb'
  | 'laser'
  | 'maestro'
  | 'mastercard'
  | 'mir'
  | 'troy'
  | 'unionpay'
  | 'visaelectron'
  | 'visa'

const cards = [
  {
    type: 'amex',
    pattern: /^3[47]/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
    lengths: [15],
    cvcLengths: [4],
    luhn: true,
    mask: '____ ______ _____',
  },
  {
    type: 'dankort',
    pattern: /^5019/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'dinersclub',
    pattern: /^(36|38|30[0-5])/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,4})?/,
    lengths: [14],
    cvcLengths: [3],
    luhn: true,
    mask: '____ ______ ____',
  },
  {
    type: 'discover',
    pattern: /^(6011|65|64[4-9]|622)/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'elo',
    pattern:
      /^401178|^401179|^431274|^438935|^451416|^457393|^457631|^457632|^504175|^627780|^636297|^636369|^636368|^(506699|5067[0-6]\d|50677[0-8])|^(50900\d|5090[1-9]\d|509[1-9]\d{2})|^65003[1-3]|^(65003[5-9]|65004\d|65005[0-1])|^(65040[5-9]|6504[1-3]\d)|^(65048[5-9]|65049\d|6505[0-2]\d|65053[0-8])|^(65054[1-9]|6505[5-8]\d|65059[0-8])|^(65070\d|65071[0-8])|^65072[0-7]|^(65090[1-9]|65091\d|650920)|^(65165[2-9]|6516[6-7]\d)|^(65500\d|65501\d)|^(65502[1-9]|6550[3-4]\d|65505[0-8])|^(65092[1-9]|65097[0-8])/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'hipercard',
    pattern: /^(384100|384140|384160|606282|637095|637568|60(?!11))/,
    format: defaultCCNumberFormat,
    lengths: [14, 15, 16, 17, 18, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'jcb',
    pattern:
      /^(308[8-9]|309[0-3]|3094[0]{4}|309[6-9]|310[0-2]|311[2-9]|3120|315[8-9]|333[7-9]|334[0-9]|35)/,
    format: defaultCCNumberFormat,
    lengths: [16, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'laser',
    pattern: /^(6706|6771|6709)/,
    format: defaultCCNumberFormat,
    lengths: [16, 17, 18, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'maestro',
    pattern: /^(50|5[6-9]|6007|6220|6304|6703|6708|6759|676[1-3])/,
    format: defaultCCNumberFormat,
    lengths: [12, 13, 14, 15, 16, 17, 18, 19],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'mastercard',
    pattern: /^(5[1-5]|677189)|^(222[1-9]|2[3-6]\d{2}|27[0-1]\d|2720)/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'mir',
    pattern: /^220[0-4][0-9][0-9]\d{10}$/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'troy',
    pattern: /^9792/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'unionpay',
    pattern: /^62/,
    format: defaultCCNumberFormat,
    lengths: [16, 17, 18, 19],
    cvcLengths: [3],
    luhn: false,
  },
  {
    type: 'visaelectron',
    pattern: /^4(026|17500|405|508|844|91[37])/,
    format: defaultCCNumberFormat,
    lengths: [16],
    cvcLengths: [3],
    luhn: true,
  },
  {
    type: 'visa',
    pattern: /^4/,
    format: defaultCCNumberFormat,
    lengths: [13, 16],
    cvcLengths: [3],
    luhn: true,
  },
] as CreditCardInfo[]

export const cardFromNumber = (
  cardNumber: string,
): CreditCardInfo | undefined => {
  const rawCardNum = (cardNumber + '').replace(/\D/g, '')
  return cards.find((card) => card.pattern.test(rawCardNum))
}

const replaceFullWidthChars = (str: string) => {
  if (str == null) {
    str = ''
  }
  const fullWidth =
    '\uff10\uff11\uff12\uff13\uff14\uff15\uff16\uff17\uff18\uff19'
  const halfWidth = '0123456789'
  let value = ''
  const chars = str.split('')
  for (let i = 0, len = chars.length; i < len; i++) {
    let char = chars[i]
    const idx = fullWidth.indexOf(char)
    if (idx > -1) {
      char = halfWidth[idx]
    }
    value += char
  }
  return value
}

export const formatCardNumber = (cardNumber: string): string => {
  let rawCardNumer = replaceFullWidthChars(cardNumber)
  rawCardNumer = rawCardNumer.replace(/\D/g, '')
  const card = cardFromNumber(rawCardNumer)
  if (!card) return rawCardNumer

  const upperLength = card.lengths[card.lengths.length - 1]
  rawCardNumer = rawCardNumer.slice(0, upperLength)
  if (card.format.global) {
    const ref = rawCardNumer.match(card.format)
    return ref != null ? ref.join(' ') : rawCardNumer
  } else {
    const groups = card.format.exec(rawCardNumer)
    if (groups == null) return rawCardNumer

    groups.shift()
    return groups.filter(Boolean).join(' ')
  }
}

export const validateCardNumber = (cardNumber: string): Boolean => {
  const rawCardNumer = (cardNumber + '').replace(/\s+|-/g, '')
  if (!/^\d+$/.test(rawCardNumer)) {
    return false
  }
  const card = cardFromNumber(rawCardNumer)
  if (!card) return false

  const ref = card.lengths.indexOf(rawCardNumer.length)
  if (ref === -1) return false

  if (card.luhn === false) return true

  return luhnCheck(rawCardNumer)
}

const luhnCheck = (num: string) => {
  let odd = true
  let sum = 0
  let digits = (num + '').split('').reverse()
  for (let i = 0, len = digits.length; i < len; i++) {
    let digit = parseInt(digits[i], 10)
    if ((odd = !odd)) {
      digit *= 2
    }
    if (digit > 9) {
      digit -= 9
    }
    sum += digit
  }
  return sum % 10 === 0
}

export const validateCardExpiry = (cardExpiry: string) => {
  const rawCardExpiry = cardExpiry.replace(/\D/g, '')
  if (rawCardExpiry.length !== 4) return 'Expected MM/YY'

  const month = Number(rawCardExpiry.slice(0, 2))
  const year = Number(`20${rawCardExpiry.slice(2, 4)}`)

  if (month < 1 || month > 12) return 'Invalid month'
  const currentMonth = moment().month() + 1
  const currentYear = moment().year()

  if (year < currentYear || (year === currentYear && month < currentMonth)) {
    return 'Expected future date'
  }
}

export const validateCardCode = (cardNumber: string, cardCode: string) => {
  const cvc = cardCode.trim()
  if (!/^\d+$/.test(cvc)) return false

  const card = cardFromNumber(cardNumber)
  if (card) {
    return card.cvcLengths.indexOf(cvc.length) >= 0
  } else {
    return cvc.length >= 3 && cvc.length <= 4
  }
}
